/* Original dependency-free interaction code. No React, Three.js, carousel package or AJAX commerce. */
(() => {
  'use strict';
  if (!window.customElements) return;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = host => reduceQuery.matches || host.dataset.motion === 'false' || document.documentElement.dataset.sbMotion === 'false';
  const listen = (node, name, handler, controller, options = {}) => node?.addEventListener(name, handler, { ...options, signal: controller.signal });

  if (!customElements.get('sb-tear-launcher')) {
    class SBTearLauncher extends HTMLElement {
      connectedCallback() {
        if (this._controller) return;
        this._controller = new AbortController();
        const ac = this._controller;
        this._toggle = this.querySelector('[data-sb-toggle]');
        this._label = this.querySelector('[data-sb-toggle-label]');
        this._targets = [...this.querySelectorAll('[data-sb-target]')];
        // A missing product must never leave a dead interactive control.
        if (!this._toggle || this._targets.length !== 2) return;
        this.dataset.enhanced = 'true';
        this.setOpen(false);
        listen(this._toggle, 'click', event => this.setOpen(this.dataset.open !== 'true', event.detail === 0), ac);
        listen(this, 'keydown', event => {
          if (event.key === 'Escape' && this.dataset.open === 'true') {
            event.preventDefault(); this.setOpen(false); this._toggle.focus({ preventScroll: true });
          }
        }, ac);
        listen(document, 'pointerdown', event => {
          if (this.dataset.open === 'true' && !this.contains(event.target)) this.setOpen(false);
        }, ac);
      }
      setOpen(open, focusFirst = false) {
        clearTimeout(this._burstTimer);
        this.dataset.open = String(open);
        this._toggle.setAttribute('aria-expanded', String(open));
        this._toggle.setAttribute('aria-label', open ? 'Close product choices' : 'Choose a T-shirt or hoodie');
        if (this._label) this._label.textContent = open ? 'Close' : 'Tap to choose';
        this._targets.forEach(target => {
          target.inert = !open;
          target.tabIndex = open ? 0 : -1;
          target.setAttribute('aria-hidden', String(!open));
        });
        delete this.dataset.burst;
        if (open && !reduced(this)) {
          this.dataset.burst = 'true';
          this._burstTimer = setTimeout(() => delete this.dataset.burst, 680);
        }
        if (open && focusFirst) requestAnimationFrame(() => {
          if (this.isConnected && this.dataset.open === 'true') this._targets[0]?.focus({ preventScroll: true });
        });
      }
      disconnectedCallback() {
        this._controller?.abort(); this._controller = null;
        clearTimeout(this._burstTimer);
      }
    }
    customElements.define('sb-tear-launcher', SBTearLauncher);
  }

  if (!customElements.get('sb-product-flick')) {
    class SBProductFlick extends HTMLElement {
      connectedCallback() {
        if (this._controller) return;
        this._controller = new AbortController();
        const ac = this._controller;
        this._stage = this.querySelector('[data-sb-stage]');
        this._slides = [...this.querySelectorAll('[data-sb-slide]')];
        this._pagination = this.querySelector('[data-sb-pagination]');
        this._status = this.querySelector('[data-sb-status]');
        this._previous = this.querySelector('[data-sb-prev]');
        this._next = this.querySelector('[data-sb-next]');
        this._index = 0; this._token = 0; this._busy = false;
        this._animations = []; this._wheelTotal = 0; this._lastWheel = 0;
        this._lastWheelInput = 0; this._suppressUntil = 0;
        if (!this._stage || this._slides.length === 0) {
          if (this._stage) this._stage.removeAttribute('tabindex');
          return;
        }
        this.dataset.enhanced = 'true';
        this._pagination?.replaceChildren();
        this._dots = this._slides.map((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button'; dot.className = 'sbui-flick__dot';
          dot.setAttribute('aria-label', `Show ${slide.dataset.label || `item ${i + 1}`}`);
          listen(dot, 'click', () => { this.go(i); this._stage.focus({ preventScroll: true }); }, ac);
          this._pagination?.append(dot);
          slide.setAttribute('aria-label', `${i + 1} of ${this._slides.length}: ${slide.dataset.label || 'Item'}`);
          return dot;
        });
        this.settle(false);
        listen(this._previous, 'click', () => { this.step(-1); this._stage.focus({ preventScroll: true }); }, ac);
        listen(this._next, 'click', () => { this.step(1); this._stage.focus({ preventScroll: true }); }, ac);
        listen(this._stage, 'keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
          if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
          if (event.key === 'Home') { event.preventDefault(); this.go(0); }
          if (event.key === 'End') { event.preventDefault(); this.go(this._slides.length - 1); }
        }, ac);
        listen(this._stage, 'dragstart', event => event.preventDefault(), ac);
        listen(this._stage, 'pointerdown', event => this.pointerDown(event), ac);
        listen(this._stage, 'pointermove', event => this.pointerMove(event), ac);
        listen(this._stage, 'pointerup', event => this.pointerUp(event), ac);
        listen(this._stage, 'pointercancel', () => this.resetDrag(), ac);
        // Touch starts with implicit capture on the hit child. Transferring capture
        // to the stage bubbles that child's lost event; it must not cancel the swipe.
        listen(this._stage, 'lostpointercapture', event => {
          if (event.target === this._stage && this._drag?.id === event.pointerId) this.resetDrag();
        }, ac);
        listen(this._stage, 'click', event => {
          if (performance.now() < this._suppressUntil) { event.preventDefault(); event.stopPropagation(); }
        }, ac, { capture: true });
        // Only this focused stage listens non-passively. The rest of the page scrolls normally.
        listen(this._stage, 'wheel', event => this.wheel(event), ac, { passive: false });
        listen(reduceQuery, 'change', () => this.settle(), ac);
        this._motionObserver = new MutationObserver(() => { if (reduced(this)) this.settle(); });
        this._motionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-sb-motion'] });
      }
      update(announce = true) {
        this._dots?.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this._index)));
        const loop = this.dataset.loop !== 'false';
        const single = this._slides.length < 2;
        if (this._previous) this._previous.disabled = single || (!loop && this._index === 0);
        if (this._next) this._next.disabled = single || (!loop && this._index === this._slides.length - 1);
        this.querySelector('[data-sb-controls]')?.toggleAttribute('hidden', single);
        this._pagination?.toggleAttribute('hidden', single);
        this.dataset.index = String(this._index);
        if (announce && this._status) this._status.textContent = `${this._slides[this._index]?.dataset.label || 'Item'}. ${this._index + 1} of ${this._slides.length}.`;
      }
      settle(announce = true) {
        this._token = (this._token || 0) + 1;
        this._animations?.forEach(animation => animation.cancel()); this._animations = [];
        this._busy = false;
        this._slides?.forEach((slide, i) => {
          slide.hidden = i !== this._index;
          slide.inert = i !== this._index;
          slide.setAttribute('aria-hidden', String(i !== this._index));
        });
        this.resetDrag(); this.update(announce);
      }
      step(direction) {
        const n = this._slides.length;
        let next = this._index + direction;
        if (this.dataset.loop !== 'false') next = (next + n) % n;
        return this.go(next, direction);
      }
      go(next, direction = next > this._index ? 1 : -1) {
        if (this._busy || next < 0 || next >= this._slides.length || next === this._index) return false;
        const previous = this._slides[this._index];
        const incoming = this._slides[next];
        this._index = next;
        this.resetDrag();
        if (previous.contains(document.activeElement)) this._stage.focus({ preventScroll: true });
        previous.inert = true; previous.setAttribute('aria-hidden', 'true');
        incoming.hidden = false; incoming.inert = false; incoming.setAttribute('aria-hidden', 'false');
        this.update();
        if (reduced(this) || typeof incoming.animate !== 'function') { this.settle(false); return true; }
        this._busy = true;
        const token = ++this._token;
        const distance = Math.min(320, this._stage.clientWidth * .45);
        try {
          const out = previous.animate([
            { opacity: 1, transform: 'translateX(0) rotate(0deg)' },
            { opacity: 0, transform: `translateX(${-direction * distance}px) rotate(${-direction * 9}deg)` }
          ], { duration: 260, easing: 'cubic-bezier(.4,0,.8,.3)', fill: 'both' });
          const enter = incoming.animate([
            { opacity: 0, transform: `translateX(${direction * distance}px) rotate(${direction * 7}deg) scale(.94)` },
            { opacity: 1, transform: 'translateX(0) rotate(0deg) scale(1)' }
          ], { duration: 430, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'both' });
          this._animations = [out, enter];
          Promise.allSettled(this._animations.map(a => a.finished)).then(() => {
            if (this.isConnected && token === this._token) this.settle(false);
          });
        } catch (_error) { this.settle(false); }
        return true;
      }
      pointerDown(event) {
        if (event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0) || this._busy || this._slides.length < 2) return;
        if (event.target.closest('button')) return;
        this._drag = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dx: 0, moved: false };
      }
      pointerMove(event) {
        const d = this._drag;
        if (!d || d.id !== event.pointerId) return;
        const dx = event.clientX - d.x, dy = event.clientY - d.y;
        if (!d.moved && Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { this.resetDrag(); return; }
        if (!d.moved && Math.abs(dx) > 9 && Math.abs(dx) > Math.abs(dy)) {
          d.moved = true; this.dataset.dragging = 'true';
          this._stage.focus({ preventScroll: true });
          try { this._stage.setPointerCapture(event.pointerId); } catch (_error) { /* safe fallback */ }
        }
        if (!d.moved) return;
        d.dx = dx;
        if (!reduced(this)) {
          const amount = Math.max(-100, Math.min(100, dx * .45));
          this.style.setProperty('--sbui-drag-x', `${amount}px`);
          this.style.setProperty('--sbui-drag-r', `${amount * .035}deg`);
        }
      }
      pointerUp(event) {
        const d = this._drag;
        if (!d || d.id !== event.pointerId) return;
        const elapsed = Math.max(16, performance.now() - d.time);
        const change = d.moved && (Math.abs(d.dx) > 34 || (Math.abs(d.dx) > 14 && Math.abs(d.dx / elapsed) > .3));
        const direction = d.dx < 0 ? 1 : -1;
        if (d.moved) this._suppressUntil = performance.now() + 250;
        this.resetDrag();
        if (change) this.step(direction);
      }
      resetDrag() {
        const id = this._drag?.id;
        this._drag = null; delete this.dataset.dragging;
        this.style.removeProperty('--sbui-drag-x'); this.style.removeProperty('--sbui-drag-r');
        if (id !== undefined) { try { if (this._stage?.hasPointerCapture(id)) this._stage.releasePointerCapture(id); } catch (_error) { /* element may be detached */ } }
      }
      wheel(event) {
        if (this.dataset.wheel === 'false' || event.ctrlKey || !this._stage.contains(document.activeElement) || this._slides.length < 2) return;
        const rect = this._stage.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > innerHeight) return;
        const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        const delta = raw * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this._stage.clientHeight : 1);
        if (!delta) return;
        const direction = Math.sign(delta);
        // Deliberately do not loop with the wheel. At either end scrolling escapes back to the page.
        if ((direction < 0 && this._index === 0) || (direction > 0 && this._index === this._slides.length - 1)) { this._wheelTotal = 0; return; }
        const now = performance.now();
        if (event.cancelable) event.preventDefault();
        if (this._busy || now - this._lastWheel < 500) return;
        if (now - this._lastWheelInput > 180 || Math.sign(this._wheelTotal) !== direction) this._wheelTotal = 0;
        this._lastWheelInput = now; this._wheelTotal += delta;
        if (Math.abs(this._wheelTotal) >= 65) {
          this._wheelTotal = 0;
          if (this.go(this._index + direction, direction)) this._lastWheel = now;
        }
      }
      disconnectedCallback() {
        this._token = (this._token || 0) + 1;
        this._controller?.abort(); this._controller = null;
        this._motionObserver?.disconnect();
        this._animations?.forEach(animation => animation.cancel());
        this.resetDrag();
      }
    }
    customElements.define('sb-product-flick', SBProductFlick);
  }
})();
